/**
 * Param/response adapter for the unified opportunities endpoint (FIR-6995).
 *
 * The unified endpoint was designed for parity with the legacy per-vertical
 * tables, so MOST filter/sort/page param names and segment field names already
 * match what the existing tables send and what the column definitions read. This
 * adapter handles only the handful of names that genuinely differ, keeping
 * everything else identity:
 *
 *  - Outgoing params ({@link adaptUnifiedListParams}): the unified filterset
 *    already speaks `name`/`domain` (icontains), range `_greater`/`_less`,
 *    repeated `__in`-style array params, the `type` all/one/neither toggle, and
 *    `sort` / `-sort`. So this is near-identity per target_type — it applies the
 *    handful of confirmed divergences (notably stripping K12's vertical-prefixed
 *    `defaultSort`) and is the single seam where any future divergence is
 *    centralized rather than scattered across table wrappers.
 *
 *  - Incoming rows ({@link adaptUnifiedItem}): the backend already COALESCEs the
 *    CRM override into `name`/`domain`, but the legacy columns key on
 *    `overriden_name`/`overriden_domain`. We mirror the unified display values
 *    onto those legacy keys so the existing column cells render without change.
 *    Segment fields keep their legacy names (emitted as-is by the providers), so
 *    they pass through untouched.
 */

export type UnifiedListParams = Record<string, unknown>

/** Backend `unified_target_type` values (mirror of {@link UnifiedTargetType}). */
type TargetType =
  | 'restaurant'
  | 'lodging'
  | 'cstore'
  | 'university'
  | 'k12'
  | 'hospital'
  | 'nursing_home'

/**
 * Per-target_type SORT-key renames.
 *
 * The `sort` param is the table's column `id` (with an optional leading `-` for
 * descending — see `CommonTable` `sortParams`). The unified endpoint's
 * `OPP_SORT_FIELDS` + each provider's `sort_fields` keys are UNPREFIXED, and the
 * shared column-id convention (`commonColumnHelpers.getTableColumn` sets
 * `id: <unprefixed column key>`) already emits unprefixed ids — so column-header
 * clicks and the sort dropdown already send the right key.
 *
 * The one genuine divergence is K12's hand-written `defaultSort` literal
 * `k12district__estimated_meals_per_day` (K12Table.tsx), which is
 * vertical-prefixed and does NOT match the unified key `estimated_meals_per_day`.
 * Under the flag that prefixed key is unknown to the unified ordering and falls
 * back to PK, so the table loads unsorted until the user clicks a column. We
 * strip the K12 vertical prefix so the default sort resolves.
 *
 * Modelled as a prefix-strip list (not a 1:1 map) so it also catches the
 * `k12district.` accessor-path form, should any K12 sort value ever carry it.
 */
const SORT_PREFIXES_TO_STRIP: Partial<Record<TargetType, readonly string[]>> = {
  k12: ['k12district__', 'k12district.'],
}

/**
 * Per-target_type FILTER-param renames: legacy param name -> unified accepted
 * name. EMPTY today — every filter param the legacy filtersets actually send
 * (verified against each `detail_providers/<vertical>.py` `filter_conditions`
 * and `UnifiedOppsFilterSet`) either matches the unified accepted name exactly
 * or is a filter the unified endpoint does not (yet) accept. The latter are NOT
 * renames — they have no unified target — so they're left to pass through and
 * tracked for behavioral QA rather than guessed at here. This map is the seam to
 * add a rename the moment a genuine 1:1 divergence is confirmed.
 */
const FILTER_RENAMES: Partial<
  Record<TargetType, Readonly<Record<string, string>>>
> = {}

/** Strip a known vertical sort prefix, preserving a leading `-` (descending). */
function stripSortPrefix(
  sortValue: string,
  prefixes: readonly string[]
): string {
  const descending = sortValue.startsWith('-')
  const key = descending ? sortValue.slice(1) : sortValue
  for (const prefix of prefixes) {
    if (key.startsWith(prefix)) {
      const stripped = key.slice(prefix.length)
      return descending ? `-${stripped}` : stripped
    }
  }
  return sortValue
}

/**
 * Translate a table's outgoing filter/sort/page params to the unified
 * endpoint's param names/shapes, per `target_type`.
 *
 * Most names already align (the unified filterset/sort vocabulary was chosen for
 * parity), so this is near-identity. It applies only the confirmed divergences:
 * stripping a vertical-prefixed sort key (e.g. K12's prefixed `defaultSort`) and
 * any per-target_type filter rename registered in {@link FILTER_RENAMES}.
 *
 * Returns a shallow copy so callers never mutate the store-owned param object.
 *
 * @param legacyParams the table's outgoing params
 * @param targetType resolved unified target_type (omit for pure pass-through)
 */
export function adaptUnifiedListParams(
  legacyParams: UnifiedListParams,
  targetType?: TargetType
): UnifiedListParams {
  const adapted: UnifiedListParams = { ...legacyParams }

  if (targetType === undefined) {
    return adapted
  }

  const sortPrefixes = SORT_PREFIXES_TO_STRIP[targetType]
  if (sortPrefixes && typeof adapted['sort'] === 'string') {
    adapted['sort'] = stripSortPrefix(adapted['sort'] as string, sortPrefixes)
  }

  const renames = FILTER_RENAMES[targetType]
  if (renames) {
    for (const [from, to] of Object.entries(renames)) {
      if (from in adapted && !(to in adapted)) {
        adapted[to] = adapted[from]
        delete adapted[from]
      }
    }
  }

  return adapted
}

/**
 * Shared-field renames applied to every unified row so legacy column
 * definitions (which key on the override fields) resolve. The unified `name` /
 * `domain` already carry the COALESCEd display value from the backend.
 */
const SHARED_FIELD_ALIASES: ReadonlyArray<readonly [from: string, to: string]> =
  [
    ['name', 'overriden_name'],
    ['domain', 'overriden_domain'],
  ]

/**
 * JSONB array columns the unified serializer emits as `[{ id, name }]`
 * (`unified_opportunity_serializers.UnifiedOpportunityListSerializer`).
 *
 * The shared pill cells in `CommonColumns`/`StagePill` read each item by a
 * column-specific key, NOT a uniform `name`, so a raw pass-through of the
 * unified `{ id, name }` shape can crash the cell (FIR-6995 e2e: `TaglistCell`'s
 * sort did `item.tag.localeCompare(...)` and `item.tag` was `undefined`).
 *
 * The reshape below maps the unified `name` onto the exact key each cell reads,
 * matching the shape the legacy per-vertical endpoints fed those cells:
 *
 *  | column         | unified item   | cell reads      | reshape applied               |
 *  |----------------|----------------|-----------------|-------------------------------|
 *  | `taglist`      | `{id, name}`   | `.tag` (`Tag`)  | `{id, tag: name}`             |
 *  | `contact_tags` | `{id, name}`   | `.tag` (`Tag`)  | `{id, tag: name}`             |
 *  | `distributors` | `{id, name}`   | `.name`         | none (key already matches)    |
 *  | `gpos`         | `{id, name}`   | `.name`         | none (key already matches)    |
 *  | `campaigns`    | `{id, name}`   | `.name`         | none (key already matches)    |
 *  | `sales_stages` | `{id, name}`   | `.id` (via deals)| none (resolved by id)        |
 *  | `account_owners`| `{id, name}`  | `.id` (via deals)| none (resolved by id)        |
 *
 * We reshape (rather than reduce to `taglistIds`) so the same row works for
 * every consumer regardless of whether it passes the array object or maps it to
 * ids — the object simply carries BOTH `id` and the cell-read key. Columns whose
 * cell already reads `name`/`id` are intentionally left untouched.
 */
type UnifiedArrayItem = { id?: number; name?: string } & Record<string, unknown>

/** Columns whose `name` must be mirrored onto a different cell-read key. */
const ARRAY_COLUMN_KEY_REMAP: Readonly<Record<string, string>> = {
  // Tag cells sort/render by `item.tag` (the `Tag`/`ContactTag` shape).
  taglist: 'tag',
  contact_tags: 'tag',
}

/**
 * Reshape one unified `[{ id, name }]` array column onto the key the cell reads,
 * preserving every other field on each item (and `id`). Non-array / empty values
 * pass through untouched.
 */
function reshapeArrayColumn(value: unknown, cellKey: string): unknown {
  if (!Array.isArray(value)) return value
  return value.map((item) => {
    if (item == null || typeof item !== 'object') return item
    const obj = item as UnifiedArrayItem
    // Idempotent: if the cell key is already populated, leave it.
    if (obj[cellKey] !== undefined) return obj
    return { ...obj, [cellKey]: obj.name }
  })
}

/**
 * Map a unified response row to the shape the existing column definitions
 * expect. Mirrors the COALESCEd shared display fields onto their legacy keys,
 * reshapes the JSONB array columns onto each cell's read key (see
 * {@link ARRAY_COLUMN_KEY_REMAP}); segment fields (already legacy-named by the
 * providers) pass through.
 */
export function adaptUnifiedItem<T extends Record<string, unknown>>(
  item: T
): T & Record<string, unknown> {
  const adapted: Record<string, unknown> = { ...item }
  for (const [from, to] of SHARED_FIELD_ALIASES) {
    if (item[from] !== undefined && adapted[to] === undefined) {
      adapted[to] = item[from]
    }
  }
  for (const [column, cellKey] of Object.entries(ARRAY_COLUMN_KEY_REMAP)) {
    if (item[column] !== undefined) {
      adapted[column] = reshapeArrayColumn(item[column], cellKey)
    }
  }
  return adapted as T & Record<string, unknown>
}

/**
 * Per-vertical row shaping (FIR-6995).
 *
 * The unified endpoint emits each vertical's segment columns FLAT at the row top
 * level (`institution_name`, `address`, `lea_name`, …). The legacy per-vertical
 * column definitions, however, read those columns through a NESTED accessor keyed
 * on the vertical's detail object (`university.address`, `k12district.lea_name`,
 * `hospital.id`, …) and build the open-detail link from an id inside that nested
 * object. So a generic flat row would leave every segment column and the detail
 * link blank.
 *
 * {@link adaptUnifiedItemForVertical} re-nests the flat unified row under the
 * vertical's detail key (the same shape the legacy endpoint returned) WITHOUT
 * touching the shared top-level fields the common columns read flat (`campaigns`,
 * `deals`, `taglist`, `contact_count`, `note_count`, `deal_count`,
 * `distributors`, `gpos`). It also:
 *
 *  - sets top-level `id = company_id`, because the table's row actions (select,
 *    tag, create-deal, push-to-campaign) read the chain-proxy id off `id` — which
 *    in the legacy contract IS the ContactCompany (chain proxy) id, i.e.
 *    `company_id` on the unified row (NOT the unified row's own `id`).
 *  - sets the nested detail id (`<nestKey>.id`, or `<nestKey>.institution_id` for
 *    university) from `detail_id`, but ONLY when `detail_id` is present, so rows
 *    with no detail row fall back to the legacy `/company/<id>` link branch.
 *
 * Field names match because the providers mirror the legacy serializer field
 * names (§3.4 / §4.4): the flat unified `address` becomes nested `university.address`.
 */
export interface VerticalRowConfig {
  /** Detail-object key the legacy columns nest under (e.g. `"university"`). */
  nestKey: string
  /**
   * Field inside the nested detail object the name cell reads to build the
   * open-detail link. Defaults to `"id"`; university reads `"institution_id"`.
   */
  detailLinkIdField?: string
  /**
   * Extra top-level keys (in addition to `id`) to mirror from `company_id`. The
   * restaurant ChainsTable keys all of its row actions off `contact_company_id`
   * rather than `id`, so it sets `["contact_company_id"]`.
   */
  companyIdAliases?: readonly string[]
}

/**
 * Re-nest a flat unified row into the shape a vertical's legacy columns expect.
 *
 * Wraps {@link adaptUnifiedItem}, then re-nests segment columns under
 * `config.nestKey`, mirrors `company_id` onto `id` (+ any `companyIdAliases`),
 * and seeds the nested detail-link id from `detail_id`. See the
 * {@link VerticalRowConfig} doc above for the full rationale and field mapping.
 */
export function adaptUnifiedItemForVertical<T extends Record<string, unknown>>(
  item: T,
  config: VerticalRowConfig
): T & Record<string, unknown> {
  const base = adaptUnifiedItem(item)
  const companyId = item['company_id']
  const detailId = item['detail_id']
  const linkIdField = config.detailLinkIdField ?? 'id'
  const hasDetail = detailId !== null && detailId !== undefined

  // Re-nest the flat segment fields under the detail key so the legacy nested
  // accessors (`<nestKey>.<field>`) resolve. The nested object's link id field
  // (`id` or `institution_id`) is the detail row id.
  const nested: Record<string, unknown> = hasDetail
    ? { ...item, [linkIdField]: detailId }
    : { ...item }

  const companyIdAliases: Record<string, unknown> = {}
  for (const alias of config.companyIdAliases ?? []) {
    companyIdAliases[alias] = companyId ?? base[alias]
  }

  return {
    ...base,
    // Chain-proxy id the row actions target (see doc above).
    id: companyId ?? base['id'],
    ...companyIdAliases,
    // Only attach the nested detail object when there's a backing detail row;
    // otherwise leave it absent so the name cell takes the user-generated branch.
    ...(hasDetail ? { [config.nestKey]: nested } : {}),
  } as T & Record<string, unknown>
}
